import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {inject, observer} from 'mobx-react';
import {action, observable} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import {injectIntl} from 'react-intl';
import MediaSelector from '../../../components/mediaSelector';
import {BPM_MAX, BPM_MIN, DEFAULT_BEATS_METER, DEFAULT_BEATS_PER_BAR, genres, instruments, keys, scale, TSArray} from '../../../constants/constants';
import {actions, category, DeviceType, events, LessonNotificationType, UploadType} from '../../../constants/enums';
import {customEvent} from '../../../utils/analyticsUtil';
import UploadFormDesktop from './UploadFormDesktop';
import UploadFormMobile from './UploadFormMobile';

@injectIntl
@inject('audioStore', 'uploadTrackStore', 'looperStore', 'recorderStore', 'projectStore')
@observer
class UploadTrackForm extends Component {
    @observable keys = keys.map(item => ({value: item.name, label: item.name, selected: false}));
    @observable signatures = TSArray.map(item => ({...item, selected: false}));
    @observable scales = scale.map((item) => {
        return {
            label: this.props.intl.formatMessage({id: `upload.form.scales.${item.name}`}),
            value: item.name,
            selected: false
        };
    });
    @observable validated = false;
    @observable audioFileValid = false;
    instruments = [];
    genres = [];
    @observable addToFeed = false;
    @observable forceMono = false;
    @observable splitAudio = false;
    @observable showAdvanced = false;

    componentWillMount() {
        const {looperStore, uploadTrackStore, intl: {formatMessage: translate}} = this.props;
        this.instruments = instruments.map((instrument) => {
            return {
                value: instrument.name,
                label: translate({id: `upload.form.instruments.${instrument.name}`})
            };
        });
        this.genres = genres.map((genre) => {
            return {
                value: genre.name,
                label: translate({id: `upload.form.genres.${genre.name}`})
            };
        });
        if (uploadTrackStore.uploadType !== UploadType.EditFile && looperStore.leaderTrack.id) {
            uploadTrackStore.initFormByPreviousTrack(looperStore.leaderTrack);
        }
    }

    @action toggleAdvanced = () => {
        this.showAdvanced = !this.showAdvanced;
    };

    onChangeScale = ({value}, index, state) => {
        this.scales.forEach((el) => {
            el.selected = false;
        });
        this.scales[index].selected = state;
        this.props.uploadTrackStore.onChange('scale', state ? value : '');
    };

    onChangeKey = ({value}, index, state) => {
        this.keys.forEach((el) => {
            el.selected = false;
        });
        this.keys[index].selected = state;
        this.props.uploadTrackStore.onChange('key', state ? value : '');
    };

    onChangeSignature = ({value}, index, state) => {
        this.signatures.forEach((el) => {
            el.selected = false;
        });
        this.signatures[index].selected = state;
        this.props.uploadTrackStore.onChange('tsBeatsPerBar', state ? value[0] : DEFAULT_BEATS_PER_BAR);
        this.props.uploadTrackStore.onChange('tsBeatsMeter', state ? value[1] : DEFAULT_BEATS_METER);
    };

    handleTrackName = (name, value) => {
        this.props.uploadTrackStore.onChange(name, value);
    };

    filterCharacters = (newValue, prevValue) => {
        const reg = new RegExp('^\\d+$|^$');
        return (reg.test(newValue)) ? newValue : prevValue;
    };

    @action handleInputChange = (name, value) => {
        const {uploadTrackStore, intl: {formatMessage: translate}} = this.props;
        const {fields} = uploadTrackStore.form;

        uploadTrackStore.onChange(name, value);
        if (uploadTrackStore.uploadType === UploadType.Record) {
            this.props.changeRecordBpm(value);
        }

        if (fields.bpm.value === '' && fields.bpm.error !== '') {
            fields.bpm.error = translate({id: 'upload.form.error.required'});
        } else {
            fields.bpm.error = '';
        }
    };

    @action submit = async (event) => {
        const {recorderStore, uploadTrackStore, projectStore, onSubmitSuccess, intl: {formatMessage: translate}} = this.props;
        const {form, validateForm} = uploadTrackStore;
        event.preventDefault();
        this.validated = true;

        if (form.fields.bpm.value === '') {
            form.fields.bpm.error = translate({id: 'upload.form.error.required'});
        } else {
            form.fields.bpm.error = '';
        }

        if (uploadTrackStore.uploadType === UploadType.Record && recorderStore.recording) {
            // fake for for to be valid
            form.fields.file.value = recorderStore.recording;
        }


        if (validateForm()) {
            uploadTrackStore.setUploadType(uploadTrackStore.uploadType)
            uploadTrackStore.handleSubmitTrack();
            onSubmitSuccess();
            projectStore.addNotificationToLesson(LessonNotificationType.AudioAddedToLesson);
        }
    };

    @action handleClearFileInput = () => {
        const {audioStore, uploadTrackStore} = this.props;
        uploadTrackStore.clearFileInput();
        audioStore.deleteAudio();
        this.validated = false;
        this.audioFileValid = false;
        uploadTrackStore.setPreviewError(false);
    };

    @action onFileChange = (target) => {
        const {audioStore, uploadTrackStore} = this.props;
        uploadTrackStore.setPreviewError(false);
        uploadTrackStore.onFileChange('file', target[0].name, target);
        // todo for now we need to make sure there's no error
        audioStore.createAudioUrl(target[0]);
        this.validated = false;
        this.audioFileValid = true;
    };

    @action onAttachmentChange = (target) => {
        customEvent(events.CustomEvent, {category: category.Upload, action: actions.AddAttachment});
        const {uploadTrackStore} = this.props;
        uploadTrackStore.onAttachmentChange('attachments', target, target);
    };

    @action onAttachmentDelete = (filename) => {
        const {uploadTrackStore} = this.props;
        uploadTrackStore.onAttachmentDelete(filename);
    };

    @action onFileError = () => {
        customEvent(events.CustomEvent, {category: category.Upload, action: actions.AddFileError});
        this.props.uploadTrackStore.setPreviewError(true);
    };

    @action handleAddToFeedClick = () => {
        this.addToFeed = !this.addToFeed;
        this.props.uploadTrackStore.onChange('addToFeed', this.addToFeed);
    };

    @action handleForceMono = () => {
        this.forceMono = !this.forceMono;
        this.props.uploadTrackStore.onChange('forceMono', this.forceMono);
    };

    @action handleSplitAudio = () => {
        this.splitAudio = !this.splitAudio;
        this.props.uploadTrackStore.onChange('splitAudio', this.splitAudio);
    };

    handleBlur = () => {
        const {form, onChange} = this.props.uploadTrackStore;
        const {value} = form.fields.bpm;
        if (value > BPM_MAX) {
            onChange('bpm', BPM_MAX);
        } else if (value && value < BPM_MIN) {
            onChange('bpm', BPM_MIN);
        }
    };

    onSelectGenre = (e) => {
        const {setChip, removeChip} = this.props.uploadTrackStore;
        if (e && e.value) {
            customEvent(events.CustomEvent, {category: category.Upload, action: actions.AddGenre});
            setChip(e.value, 'genre');
        } else {
            customEvent(events.CustomEvent, {category: category.Upload, action: actions.RemoveGenre});
            removeChip(e, 0, 'genre');
        }
    };

    onSelectInstrument = (e) => {
        const {setChip, removeChip} = this.props.uploadTrackStore;
        if (e && e.value) {
            setChip(e.value, 'instruments');
        } else {
            removeChip(e, 0, 'instruments');
        }
    };

    ignoreSubmit = (e) => {
        e.preventDefault();
    };

    render() {
        const {audioStore, uploadTrackStore, header, showFileUpload, toggleBpm} = this.props;
        const {form, isUploading, onBlur, uploadType, previewError} = uploadTrackStore;
        const {trackUrl, setAudioRef, togglePlay} = audioStore;
        const {fields} = form;
        const signature = fields.tsBeatsPerBar.value ? `${fields.tsBeatsPerBar.value}/${fields.tsBeatsMeter.value}` : '';
        const activeGenre = this.genres.find(g => g.value === (!isEmpty(fields.genre.value) && fields.genre.value[0]));
        const activeInstrument = this.instruments.find(i => i.value === (!isEmpty(fields.instruments.value) && fields.instruments.value[0]));
        const attachments = fields.attachments.value ? fields.attachments.value.slice() : [];
        return (
            <div className="upload-form-container">
                <form className="upload-track-form" onSubmit={this.ignoreSubmit}>
                    <MediaSelector devices={[DeviceType.Desktop, DeviceType.Tablet]}>
                        <UploadFormDesktop
                            header={header}
                            setAudioRef={setAudioRef}
                            togglePlay={togglePlay}
                            trackUrl={trackUrl}
                            showFileUpload={showFileUpload}
                            form={form}
                            fields={fields}
                            previewError={previewError}
                            attachments={attachments}
                            toggleBpm={toggleBpm}
                            activeGenre={activeGenre}
                            activeInstrument={activeInstrument}
                            signature={signature}
                            onBlur={onBlur}
                            uploadType={uploadType}
                            onFileError={this.onFileError}
                            handleClearFileInput={this.handleClearFileInput}
                            onFileChange={this.onFileChange}
                            validated={this.validated}
                            onAttachmentChange={this.onAttachmentChange}
                            onAttachmentDelete={this.onAttachmentDelete}
                            handleTrackName={this.handleTrackName}
                            keys={this.keys}
                            onChangeKey={this.onChangeKey}
                            scales={this.scales}
                            onChangeScale={this.onChangeScale}
                            onChangeSignature={this.onChangeSignature}
                            signatures={this.signatures}
                            handleBlur={this.handleBlur}
                            handleInputChange={this.handleInputChange}
                            filterCharacters={this.filterCharacters}
                            onSelectGenre={this.onSelectGenre}
                            genres={this.genres}
                            onSelectInstrument={this.onSelectInstrument}
                            instruments={this.instruments}
                            addToFeed={this.addToFeed}
                            handleAddToFeedClick={this.handleAddToFeedClick}
                            forceMono={this.forceMono}
                            handleForceMono={this.handleForceMono}
                            splitAudio={this.splitAudio}
                            handleSplitAudio={this.handleSplitAudio}
                            submit={this.submit}
                            audioFileValid={this.audioFileValid}
                        />
                    </MediaSelector>
                    <MediaSelector devices={[DeviceType.Mobile]}>
                        <UploadFormMobile
                            header={header}
                            setAudioRef={setAudioRef}
                            togglePlay={togglePlay}
                            trackUrl={trackUrl}
                            showFileUpload={showFileUpload}
                            form={form}
                            fields={fields}
                            previewError={previewError}
                            attachments={attachments}
                            toggleBpm={toggleBpm}
                            activeGenre={activeGenre}
                            activeInstrument={activeInstrument}
                            signature={signature}
                            isUploading={isUploading}
                            onBlur={onBlur}
                            uploadType={uploadType}
                            showAdvanced={this.showAdvanced}
                            toggleAdvanced={this.toggleAdvanced}
                            onFileError={this.onFileError}
                            handleClearFileInput={this.handleClearFileInput}
                            onFileChange={this.onFileChange}
                            validated={this.validated}
                            onAttachmentChange={this.onAttachmentChange}
                            onAttachmentDelete={this.onAttachmentDelete}
                            handleTrackName={this.handleTrackName}
                            keys={this.keys}
                            onChangeKey={this.onChangeKey}
                            scales={this.scales}
                            onChangeScale={this.onChangeScale}
                            onChangeSignature={this.onChangeSignature}
                            signatures={this.signatures}
                            handleBlur={this.handleBlur}
                            handleInputChange={this.handleInputChange}
                            filterCharacters={this.filterCharacters}
                            onSelectGenre={this.onSelectGenre}
                            genres={this.genres}
                            onSelectInstrument={this.onSelectInstrument}
                            instruments={this.instruments}
                            addToFeed={this.addToFeed}
                            handleAddToFeedClick={this.handleAddToFeedClick}
                            forceMono={this.forceMono}
                            handleForceMono={this.handleForceMono}
                            splitAudio={this.splitAudio}
                            handleSplitAudio={this.handleSplitAudio}
                            submit={this.submit}
                        />
                    </MediaSelector>
                </form>
            </div>
        );
    }
}

UploadTrackForm.propTypes = {
    onSubmitSuccess: PropTypes.func.isRequired,
};

export default UploadTrackForm;
